<template>
	<div class="region-plans-statistics">
		<!-- header -->
		<div class="d-flex align-center px-4 py-3 region-plans-statistics__header" @click="$router.back()">
			<v-icon class="me-2">{{ backIcon }}</v-icon>
			<span class="text-subtitle-1 font-weight-bold">{{ $T('title') }}</span>
		</div>

		<div class="pa-3 pb-0">
			<!-- visits rate -->
			<region-visits-rate-card
				:percentage="summary.percentage"
				:visited-count="summary.visitedCount"
				:remaining-count="summary.remainingCount"
				:date="selectedDay"
			/>
		</div>

		<!-- days picker -->
		<v-day-date
			v-model="selectedDay"
			:month="selectedMonth"
			:optional="false"
			class="my-3"
		/>

		<div class="px-3 pb-3">
			<!-- incomplete visits -->
			<v-alert
				v-if="summary.remainingCount"
				dense
				text
				border="left"
				type="warning"
				icon="mdi-calendar"
				class="text-caption region-plans-statistics__banner"
			>
				{{ $T('incomplete-visits-banner', {
					count: $options.filters.formatNumber(summary.remainingCount),
					date: moment(selectedDay).format('DD MMMM YYYY')
				}) }}
			</v-alert>

			<loader v-if="loading"/>

			<!-- regions -->
			<v-expansion-panels v-else flat class="region-expander">
				<v-expansion-panel
					v-for="region in regionRows"
					:key="region.id"
					class="region-expander__panel mb-3"
				>
					<v-expansion-panel-header hide-actions class="px-3">
						<template v-slot:default="{ open }">
							<div class="d-flex align-center justify-space-between">
								<div class="d-flex align-center flex-shrink-0">
									<span class="text-subtitle-2 font-weight-medium">{{ region.name }}</span>
									<v-icon small class="ms-1">{{ open ? 'mdi-chevron-down' : collapsedIcon }}</v-icon>
								</div>

								<div class="d-flex align-center flex-shrink-0">
									<v-progress-linear
										:color="`${rateColor(region.percentage)} darken-1`"
										background-color="grey lighten-2"
										rounded
										height="8"
										class="region-expander__bar me-2"
										:value="region.percentage"
									></v-progress-linear>

									<span class="text-caption grey--text text--darken-2 me-2">
										{{ $options.filters.formatNumber(region.visitedCount) }}/{{ $options.filters.formatNumber(region.total) }}
									</span>

									<v-chip
										x-small
										:color="`${rateColor(region.percentage)} lighten-5`"
										:text-color="`${rateColor(region.percentage)} darken-2`"
										class="font-weight-bold"
									>
										{{ $options.filters.formatNumber(region.percentage) }}%
									</v-chip>
								</div>
							</div>
						</template>
					</v-expansion-panel-header>

					<v-expansion-panel-content class="region-expander__content">
						<v-expansion-panels flat accordion class="region-expander__inner">
							<!-- visited -->
							<v-expansion-panel class="region-expander__inner-panel mb-2">
								<v-expansion-panel-header hide-actions class="px-3">
									<template v-slot:default="{ open }">
										<div class="d-flex align-center">
											<span class="text-body-2 flex-shrink-0">{{ $T('visited') }}</span>
											<v-icon small class="ms-1 flex-shrink-0">{{ open ? 'mdi-chevron-down' : collapsedIcon }}</v-icon>
											<span class="flex-grow-1 mx-2 region-expander__dashes"></span>
											<span class="text-body-2 flex-shrink-0">
												{{ $options.filters.formatNumber(region.visitedEntityIds.length) }}
											</span>
										</div>
									</template>
								</v-expansion-panel-header>

								<v-expansion-panel-content>
									<div class="d-flex flex-wrap region-expander__chips">
										<v-chip
											v-for="entityId in region.visitedEntityIds"
											:key="entityId"
											small
											color="green lighten-5"
											text-color="green darken-2"
											class="region-expander__chip"
										>
											{{ entityName(entityId) }}
										</v-chip>
									</div>
								</v-expansion-panel-content>
							</v-expansion-panel>

							<!-- not visited -->
							<v-expansion-panel class="region-expander__inner-panel">
								<v-expansion-panel-header hide-actions class="px-3">
									<template v-slot:default="{ open }">
										<div class="d-flex align-center">
											<span class="text-body-2 flex-shrink-0">{{ $T('not-visited') }}</span>
											<v-icon small class="ms-1 flex-shrink-0">{{ open ? 'mdi-chevron-down' : collapsedIcon }}</v-icon>
											<span class="flex-grow-1 mx-2 region-expander__dashes"></span>
											<span class="text-body-2 flex-shrink-0">
												{{ $options.filters.formatNumber(region.notVisitedEntityIds.length) }}
											</span>
										</div>
									</template>
								</v-expansion-panel-header>

								<v-expansion-panel-content>
									<div class="d-flex flex-wrap region-expander__chips">
										<v-chip
											v-for="entityId in region.notVisitedEntityIds"
											:key="entityId"
											small
											color="red lighten-5"
											text-color="red darken-2"
											class="region-expander__chip"
										>
											{{ entityName(entityId) }}
										</v-chip>
									</div>
								</v-expansion-panel-content>
							</v-expansion-panel>
						</v-expansion-panels>
					</v-expansion-panel-content>
				</v-expansion-panel>
			</v-expansion-panels>

			<div
				v-if="!loading && !regionRows.length"
				class="d-flex flex-column justify-center align-center grey--text text-body-1 py-10"
			>
				<v-icon x-large class="mb-3">mdi-alert-circle-outline</v-icon>
				{{ $T('no-statistics') }}
			</div>
		</div>
	</div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import moment from 'moment';
import Loader from '@/components/Loader.vue';
import VDayDate from '@/components/VDayDate.vue';
import RegionVisitsRateCard from '@/components/RegionVisitsRateCard.vue';

export default {
	name: 'RegionPlansStatistics',

	components: {
		Loader,
		VDayDate,
		RegionVisitsRateCard
	},

	data: () => ({
		loading: false,
		selectedDay: moment().format('YYYY-MM-DD'),
	}),

	watch: {
		selectedDay() {
			this.loading = true;
			this.fetchStatistics().catch(() => {}).finally(() => {
				this.loading = false;
			})
		}
	},

	computed: {
		...mapState({
			self: state => state.auth.self,
			statistics: state => state.regionPlans.statistics,
			regions: state => state.regions.regions,
			allEntities: state => state.entities.allEntities,
		}),

		...mapGetters({
			getRegionById: 'regions/getRegionById',
			summary: 'regionPlans/getRegionPlansStatisticsSummary',
		}),

		selectedMonth() {
			return moment(this.selectedDay).format('YYYY-MM')
		},

		backIcon() {
			return this.$vuetify.rtl ? 'mdi-chevron-right' : 'mdi-chevron-left'
		},

		collapsedIcon() {
			return this.$vuetify.rtl ? 'mdi-chevron-left' : 'mdi-chevron-right'
		},

		// the statistics response carries ids only - one lookup map beats a find() per chip
		entityNamesById() {
			return this.allEntities.reduce((names, entity) => {
				names[entity.id] = entity.name;
				return names;
			}, {})
		},

		regionRows() {
			return this.statistics.map(region => {
				const total = region.totalEntitiesCount || 0;
				const visitedEntityIds = region.visitedEntityIds || [];

				return {
					id: region.regionId,
					name: region.regionName || this.getRegionById(region.regionId)?.name || '',
					total,
					visitedCount: visitedEntityIds.length,
					percentage: total ? Math.round((visitedEntityIds.length / total) * 100) : 0,
					visitedEntityIds,
					notVisitedEntityIds: region.notVisitedEntityIds || [],
				}
			})
		},
	},

	methods: {
		moment,

		rateColor(percentage) {
			if (percentage < 50) return 'red';
			if (percentage < 80) return 'amber';
			return 'green';
		},

		// a soft-deleted customer can still show up in an older day's statistics
		entityName(entityId) {
			return this.entityNamesById[entityId] || this.$T('unknown-customer');
		},

		fetchStatistics() {
			return this.$store.dispatch('regionPlans/fetchStatistics', {
				fromDate: this.selectedDay,
				toDate: this.selectedDay,
				employeeIds: [this.self.employeeId],
			})
		},
	},

	created() {
		if (this.$route.query.date) this.selectedDay = this.$route.query.date;

		this.loading = true;
		Promise.all([
			this.fetchStatistics().catch(() => {}),
			// both stores are reset by other pages' beforeDestroy, which runs after this created() -
			// a length guard here would keep data that is about to be wiped, so always refetch
			this.$store.dispatch('entities/fetchAllGroupedByRegion').catch(() => {}),
			this.$store.dispatch('regions/fetchAll').catch(() => {}),
		]).finally(() => {
			this.loading = false;
		})
	},

	beforeDestroy() {
		this.$store.dispatch('regionPlans/resetStatistics');
	}
};
</script>

<style lang="scss">
.region-plans-statistics {
	&__header {
		border-bottom: 1px solid #e0e0e0;
		background-color: #fff;
	}

	&__banner {
		border-radius: 8px;
	}
}

.region-expander {
	&__panel {
		border: 1px solid #e0e0e0;
		border-radius: 12px;

		&::before {
			box-shadow: none;
		}
	}

	&__bar {
		flex: 0 0 80px;
		width: 80px;
	}

	&__content .v-expansion-panel-content__wrap {
		padding: 0 12px 12px;
	}

	&__inner-panel {
		border: 1px solid #e0e0e0;
		border-radius: 12px;

		&::before {
			box-shadow: none;
		}
	}

	&__dashes {
		border-bottom: 1px dashed #bdbdbd;
	}

	&__chips {
		gap: 8px;
	}

	&__chip {
		border: 1px solid currentColor;
	}
}
</style>
